import { AuthPageHeaderFeature } from 'src/features/AuthPageHeaderFeature';
import { useTranslation } from 'react-i18next';
import { PublicRoutes } from 'src/routes/navigation';
import {
  IconButton,
  IconLanguage,
  Container_v2 as Container,
  Typography,
  TypographyVariantsEnum,
  Button_v2 as Button,
  IconGoogle,
  IconApple,
  IconLetter,
  Loading,
  AppFooter_v2 as AppFooter,
  ModalSheet,
} from '@mindly/ui-components';
import { AppLogo, useAnalytics } from 'src/shared';
import BuildInformation from 'src/components/BuildInformation/BuildInformation';
import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './Auth.module.css';
import { isPlatform } from '@ionic/react';
import { SupportedLangsType } from 'src/shared/enums/supportedLangs.enum';
import { selectTermsOfConditions } from 'src/redux/firestoreGlobalVariables/selector';
import { setIsLinkUserExist } from 'src/redux/auth/reducer';
import { ChangeLanguageModalWithLanguageWorkInProgressFeature } from 'src/features/ChangeLanguageModalWithLanguageWorkInProgressFeature';

type DefaultAuthWidgetProps = {
  isLoading: boolean;
  isLinkUserExist: boolean;
  authType: 'apple' | 'google' | null;
  handleSignIn: () => void;
  handleGoogleSignIn: () => void;
  handleAppleSignIn: () => void;
  handleLinkClick: (url: string) => void;
  setIsOnboardingDataLoading: (isLoading: boolean) => void;
};

const DefaultAuthWidget = ({
  isLoading,
  isLinkUserExist,
  authType,
  handleSignIn,
  handleGoogleSignIn,
  handleAppleSignIn,
  handleLinkClick,
  setIsOnboardingDataLoading,
}: DefaultAuthWidgetProps) => {
  const dispatch = useDispatch();
  const { t, i18n } = useTranslation('auth');
  const { trackPredefinedEvent } = useAnalytics();

  const termsConditions = useSelector(selectTermsOfConditions);

  const [changeLanguageShow, setChangeLanguageShow] = useState(false);

  const termsOfConditionLink = useMemo(() => {
    const currentLocale = i18n.language as SupportedLangsType | 'en';

    return termsConditions?.[currentLocale] ?? termsConditions?.en;
  }, [i18n.language, termsConditions]);

  return (
    <>
      <AuthPageHeaderFeature setIsOnboardingDataLoading={setIsOnboardingDataLoading}>
        <div className="flex items-center justify-end">
          <IconButton icon={<IconLanguage />} onClick={() => setChangeLanguageShow(true)} />
        </div>
      </AuthPageHeaderFeature>
      <Container className={styles.container}>
        <div className={'flex flex-col flex-1 items-center justify-center gap-8'}>
          <AppLogo />
          <div className={'flex flex-col gap-2'}>
            <Typography variant={TypographyVariantsEnum.Title28Semi} align={'center'}>
              {localStorage.getItem('onboardingToken') ? t('auth.registration.title') : t('auth.auth.title')}
            </Typography>
            <Typography variant={TypographyVariantsEnum.Text14Regular} align={'center'} opacity={50} className={'px-6'}>
              {localStorage.getItem('onboardingToken')
                ? t('auth.registration.description')
                : t('auth.auth.description')}
            </Typography>
          </div>
          <div className={'flex flex-col gap-5 w-full'}>
            <Button
              fullWidth
              fill={'outline'}
              size={'large'}
              icon={<IconGoogle />}
              iconPosition={'start'}
              onClick={handleGoogleSignIn}
            >
              {t('auth.auth.google.button')}
            </Button>
            {!isPlatform('android') && (
              <Button
                fullWidth
                fill={'outline'}
                size={'large'}
                icon={<IconApple />}
                iconPosition={'start'}
                onClick={handleAppleSignIn}
              >
                {t('auth.auth.apple.button')}
              </Button>
            )}
            <div onClick={() => trackPredefinedEvent('LOGIN_EMAIL_BUTTON_CLICKED')}>
              <Button
                fullWidth
                fill={'outline'}
                size={'large'}
                icon={<IconLetter />}
                iconPosition={'start'}
                routerLink={PublicRoutes.EMAIL_SIGN_IN}
              >
                {t('auth.auth.email.button')}
              </Button>
            </div>
          </div>
          <Typography variant={TypographyVariantsEnum.Text13Regular} align={'center'} opacity={50}>
            {t('auth.auth.terms.text')}
            <br />
            <button
              onClick={() => handleLinkClick(`https://mindlyspace.com/${i18n.language}/privacy-policy`)}
              className={'underline outline-none appearance-none cursor-pointer'}
            >
              {t('auth.auth.terms.link.0')}
            </button>
            <br />
            <button
              onClick={() => handleLinkClick(termsOfConditionLink)}
              className={'underline outline-none appearance-none cursor-pointer'}
            >
              {t('auth.auth.terms.link.1')}
            </button>
          </Typography>
        </div>
        <Loading isOpen={isLoading} />
      </Container>
      <AppFooter transparent>
        <BuildInformation />
      </AppFooter>
      <ChangeLanguageModalWithLanguageWorkInProgressFeature
        isOpen={changeLanguageShow}
        closeModal={() => setChangeLanguageShow(false)}
      />
      <ModalSheet
        isOpen={isLinkUserExist}
        heightInPx={290}
        onIonModalDidDismiss={() => dispatch(setIsLinkUserExist(false))}
      >
        <div className={'h-full pt-8 px-5'}>
          <Typography variant={TypographyVariantsEnum.Text24Semi} align={'center'} className={'mb-3'}>
            {t('auth.login.google.title')}
          </Typography>
          <Typography variant={TypographyVariantsEnum.Text14Regular} align={'center'} opacity={50} className={'mb-8'}>
            {t('auth.login.google.description')}
          </Typography>
          <Button
            fullWidth
            fill={'outline'}
            size={'large'}
            icon={authType === 'apple' ? <IconApple /> : <IconGoogle />}
            iconPosition={'start'}
            onClick={handleSignIn}
          >
            {authType === 'apple' ? t('auth.login.apple.button') : t('auth.login.google.button')}
          </Button>
        </div>
      </ModalSheet>
    </>
  );
};

export default DefaultAuthWidget;
